// PozicovnaReporty — reporty požičovne (Mirko 18. 9. 2026: „predajňa sa sťažuje, že nevie robiť reporty
// z požičovne a filtrovať, kto čo má požičané… reporty na zákazku, zamestnanca, dátum, dobu alebo položku
// nech fungujú aj na zobrazenie, aj na generovanie do PDF a tlač, aj na e-mail“).
// Jedna strana: filtre hore, zoznam dole. Dlho nevrátené sú zvýraznené. Esc zavrie, Ctrl+P vytlačí (aj do PDF).
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { fetchAuth } from '../auth/fetchAuth.js'
import { PageHeader, SearchInput, FilterChips, ListStatusBar } from '../components/Ui.jsx'
import { BtnGhost, BtnNav } from '../components/Btn.jsx'
import { useToast } from '../components/Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS, hladaj } from '../theme.js'

const DLHO = 30                      // dlho vonku = viac ako 30 dní
const dd = (d) => (String(d || '').slice(0, 10) ? String(d).slice(0, 10).split('-').reverse().join('.') : '—')

export default function PozicovnaReporty({ vnorene = false }) {
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const [f, setF] = useState({
    zakazka: params.get('zakazka') || '', zamestnanec: '', polozka: '', od: '', do: '',
    stav: 'aktivne', min_dni: '',
  })
  const [data, setData] = useState({ riadky: [], suhrn: {}, ciselniky: { zamestnanci: [], zakazky: [], polozky: [] } })
  const [hladat, setHladat] = useState('')
  const [nacitava, setNacitava] = useState(true)
  const [posiela, setPosiela] = useState(false)
  const [okno, setOkno] = useState(null)

  const nacitaj = useCallback(() => {
    setNacitava(true)
    const q = new URLSearchParams(Object.entries(f).filter(([, v]) => v !== '' && v != null)).toString()
    fetchAuth('/api/pozicovna/report?' + q).then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(d) })
      .catch(() => toast.error('Report sa nenačítal'))
      .finally(() => setNacitava(false))
  }, [f])
  useEffect(() => { nacitaj() }, [nacitaj])

  const zoznam = useMemo(() => {
    const q = hladat.trim().toLowerCase()
    if (!q) return data.riadky
    return data.riadky.filter(r => hladaj([r.polozka, r.kod, r.osoba, r.project_id, r.zakazka_nazov, r.vyrobne_cislo]
      .filter(Boolean).join(' '), q))
  }, [data.riadky, hladat])

  const zavri = useCallback(() => navigate(-1), [navigate])
  useEffect(() => {
    if (vnorene) return                                  // vnútri Požičovne Esc zatvára modul, nie report
    const h = (e) => { if (e.key === 'Escape' && !document.querySelector('[aria-modal="true"]')) { e.preventDefault(); zavri() } }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [zavri, vnorene])

  const otvorOdoslat = () => setOkno({
    komu: '', predmet: 'Požičovňa — prehľad ' + new Date().toLocaleDateString('sk-SK'),
    text: 'Dobrý deň,\n\nposielam prehľad z požičovne podľa nastaveného filtra. Červené sú veci vonku dlhšie ako ' + DLHO + ' dní.\n\nS pozdravom\nKAMIR s.r.o.',
  })
  const posliEmailom = async () => {
    if (!okno || !okno.komu.trim()) { toast.error('Zadaj e-mail príjemcu'); return }
    setPosiela(true)
    try {
      const r = await fetchAuth('/api/pozicovna/report/odoslat', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ komu: okno.komu, predmet: okno.predmet, text: okno.text, filtre: f }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || d.ok === false) { toast.error('Odoslanie zlyhalo: ' + (d.error || 'HTTP ' + r.status)); return }
      toast.success('Odoslané na ' + (d.komu || okno.komu) + ' (' + (d.riadkov || 0) + ' riadkov)')
      setOkno(null)
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setPosiela(false) }
  }

  const th = { position: 'sticky', top: 0, zIndex: 1, background: '#eef5fa', color: COLORS.nav, fontSize: 11, fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.03em', padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #d6e6f2', whiteSpace: 'nowrap' }
  const td = { padding: '7px 10px', borderBottom: `1px solid ${COLORS.lineSoft}`, fontSize: TYPO.sizes.sm, verticalAlign: 'middle', whiteSpace: 'nowrap' }
  const pole = { padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, background: COLORS.bg, color: COLORS.text }
  const nastav = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }))

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: SPACE.sm, minHeight: 0 }}>
      <style>{`@media print {
        body * { visibility: hidden; }
        #pozicovna-tlac, #pozicovna-tlac * { visibility: visible; }
        #pozicovna-tlac { position: absolute; left: 0; top: 0; width: 100%; }
        #pozicovna-tlac .bez-tlace { display: none !important; }
      }`}</style>
      {vnorene ? (
        <div className="bez-tlace" style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, flex: 1 }}>
            Kto čo má požičané, na ktorej zákazke a ako dlho. Filtre platia aj pre tlač, PDF aj e-mail.
          </span>
          <BtnGhost onClick={() => window.print()}>Tlačiť / PDF (Ctrl+P)</BtnGhost>
          <BtnGhost onClick={otvorOdoslat}>Poslať e-mailom</BtnGhost>
        </div>
      ) : (
      <PageHeader title="Požičovňa — reporty" subtitle="Kto čo má požičané, na ktorej zákazke a ako dlho. Filtre platia aj pre tlač, PDF aj e-mail. Esc zavrie, Ctrl+P vytlačí alebo uloží do PDF.">
        <BtnGhost onClick={() => window.print()}>Tlačiť / PDF (Ctrl+P)</BtnGhost>
        <BtnGhost onClick={otvorOdoslat}>Poslať e-mailom</BtnGhost>
        <BtnNav onClick={() => navigate('/pozicovna')}>Požičovňa</BtnNav>
        <BtnGhost onClick={zavri}>Zavrieť (Esc)</BtnGhost>
      </PageHeader>
      )}

      <div className="bez-tlace" style={{ display: 'flex', gap: SPACE.sm, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>Zákazka
          <select value={f.zakazka} onChange={nastav('zakazka')} style={{ ...pole, minWidth: 190 }}>
            <option value="">Všetky zákazky</option>
            {data.ciselniky.zakazky.map(z => <option key={z.id} value={z.id}>{z.id}{z.nazov && z.nazov !== z.id ? ' · ' + z.nazov : ''}</option>)}
          </select>
        </label>
        <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>Kto má
          <select value={f.zamestnanec} onChange={nastav('zamestnanec')} style={{ ...pole, minWidth: 170 }}>
            <option value="">Všetci ľudia</option>
            {data.ciselniky.zamestnanci.map(z => <option key={z.id} value={z.id}>{z.nazov}</option>)}
          </select>
        </label>
        <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>Položka
          <select value={f.polozka} onChange={nastav('polozka')} style={{ ...pole, minWidth: 170 }}>
            <option value="">Všetko náradie</option>
            {data.ciselniky.polozky.map(z => <option key={z.id} value={z.id}>{z.nazov}</option>)}
          </select>
        </label>
        <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>Vydané od
          <input type="date" value={f.od} onChange={nastav('od')} style={pole} />
        </label>
        <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>Vydané do
          <input type="date" value={f.do} onChange={nastav('do')} style={pole} />
        </label>
        <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>Vonku aspoň (dní)
          <input type="number" min="0" value={f.min_dni} onChange={nastav('min_dni')} placeholder="napr. 30" style={{ ...pole, width: 110 }} />
        </label>
        <div style={{ minWidth: 200, flex: '0 1 260px' }}>
          <SearchInput value={hladat} onChange={v => setHladat(typeof v === 'string' ? v : (v && v.target ? v.target.value : ''))} placeholder="Hľadať náradie, človeka, zákazku…" />
        </div>
        <FilterChips value={f.stav} onChange={v => setF(s => ({ ...s, stav: v }))} options={[
          { key: 'aktivne', label: 'Nevrátené' },
          { key: 'vratene', label: 'Vrátené' },
          { key: 'vsetko', label: 'Všetko' },
        ]} />
      </div>

      <div id="pozicovna-tlac" style={{ flex: 1, minHeight: 240, overflow: 'auto', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.bg }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead><tr>
            <th style={th}>Položka</th><th style={th}>Kód</th><th style={th}>Kto má</th><th style={th}>Zákazka</th>
            <th style={th}>Od</th><th style={th}>Vrátené</th><th style={{ ...th, textAlign: 'right' }}>Dní</th>
            <th style={{ ...th, textAlign: 'right' }}>Ks</th><th style={th}>Poznámka</th>
          </tr></thead>
          <tbody>
            {nacitava ? <tr><td colSpan={9} style={{ ...td, color: COLORS.muted, padding: SPACE.lg }}>Načítavam…</td></tr>
              : zoznam.length === 0 ? <tr><td colSpan={9} style={{ ...td, color: COLORS.muted, padding: SPACE.lg }}>Nič nezodpovedá filtru.</td></tr>
                : zoznam.map(r => {
                  const dlho = !r.vratene && r.dni > DLHO
                  return (
                    <tr key={r.id} style={{ background: dlho ? 'rgba(220,38,38,0.05)' : 'transparent' }}>
                      <td style={{ ...td, fontWeight: TYPO.weights.bold }}>{r.polozka}</td>
                      <td style={{ ...td, fontFamily: TYPO.mono, color: COLORS.muted }}>{r.kod || r.vyrobne_cislo || '—'}</td>
                      <td style={td}>{r.osoba || '—'}</td>
                      <td style={td}>{r.project_id ? r.project_id + (r.zakazka_nazov ? ' · ' + r.zakazka_nazov : '') : '—'}</td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{dd(r.start_date)}</td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: r.vratene ? COLORS.text : COLORS.remove, fontWeight: r.vratene ? 400 : TYPO.weights.bold }}>
                        {r.vratene ? dd(r.return_date) : 'nevrátené'}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: dlho ? COLORS.remove : COLORS.text, fontWeight: dlho ? TYPO.weights.bold : 400 }}>{r.dni}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.qty || 1}</td>
                      <td style={{ ...td, whiteSpace: 'normal', color: COLORS.muted, fontSize: TYPO.sizes.xs, maxWidth: 240 }}>{String(r.notes || '').split('\n').filter(Boolean).slice(-1)[0] || ''}</td>
                    </tr>
                  )
                })}
          </tbody>
        </table>
      </div>

      {okno && (
        <div role="dialog" aria-modal="true" aria-label="Poslať report požičovne e-mailom" onClick={() => setOkno(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: COLORS.bg, borderRadius: RADIUS.lg, width: 'min(560px, 96vw)', padding: SPACE.lg, display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
            <b style={{ fontSize: TYPO.sizes.lg }}>Poslať report požičovne e-mailom</b>
            <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Komu (viac adries oddeľ čiarkou)
              <input value={okno.komu} onChange={e => setOkno(o => ({ ...o, komu: e.target.value }))} placeholder="meno@firma.sk"
                style={{ width: '100%', marginTop: 4, padding: '9px 11px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box' }} />
            </label>
            <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Predmet
              <input value={okno.predmet} onChange={e => setOkno(o => ({ ...o, predmet: e.target.value }))}
                style={{ width: '100%', marginTop: 4, padding: '9px 11px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box' }} />
            </label>
            <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Správa
              <textarea value={okno.text} onChange={e => setOkno(o => ({ ...o, text: e.target.value }))} rows={5}
                style={{ width: '100%', marginTop: 4, padding: '9px 11px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box', resize: 'vertical' }} />
            </label>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Ide presne to, čo je teraz vo filtri ({data.riadky.length} riadkov). Tabuľka je priamo v e-maile.</div>
            <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
              <BtnGhost onClick={() => setOkno(null)} disabled={posiela}>Zrušiť</BtnGhost>
              <BtnNav onClick={posliEmailom} disabled={posiela}>{posiela ? 'Posielam…' : 'Odoslať'}</BtnNav>
            </div>
          </div>
        </div>
      )}

      <ListStatusBar>
        {zoznam.length} {zoznam.length === 1 ? 'záznam' : zoznam.length < 5 ? 'záznamy' : 'záznamov'} ·
        {' '}{data.suhrn.kusov || 0} ks · {data.suhrn.osob || 0} ľudí · {data.suhrn.zakaziek || 0} zákaziek ·
        {' '}najdlhšie vonku {data.suhrn.najdlhsie || 0} dní · Esc zavrie · Ctrl+P vytlačí alebo uloží do PDF
      </ListStatusBar>
    </div>
  )
}
